var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { toUrlQueryString } from '../../../../backend/utils';
import { baseClientDefaults } from '@juvare/platform-sdk-js/BaseClient';
import { notifyPrefix } from '../../../clients/AlertServiceClient';
import { createClientError } from '../../../utils/ClientError';
import { getToken } from '../stores/getToken';
import { storeToken } from '../stores/storeToken';
/**
 * Maps `NotificationFilterData` (used by frontend) to `GetNotificationsRequest` (used by backend).
 */
export function mapNotificationFilterData(filterData, params) {
    var _a;
    return Object.assign({ archived: filterData.archived, read: isNotificationReadFilter(filterData), waitingForResponse: (_a = filterData.status) === null || _a === void 0 ? void 0 : _a.includes('waiting'), labels: filterData.notificationType, searchText: filterData.searchText }, params);
}
function isNotificationReadFilter(filterData) {
    var _a, _b, _c, _d;
    let read = undefined;
    if ((_a = filterData.status) === null || _a === void 0 ? void 0 : _a.includes('read')) {
        read = true;
    }
    if ((_b = filterData.status) === null || _b === void 0 ? void 0 : _b.includes('unread')) {
        read = false;
    }
    if (((_c = filterData.status) === null || _c === void 0 ? void 0 : _c.includes('read')) && ((_d = filterData.status) === null || _d === void 0 ? void 0 : _d.includes('unread'))) {
        read = undefined; // undefined tells notify service to provide all notifications
    }
    return read;
}
export class InboxClient {
    constructor(email, onTokenRequest) {
        this.email = email;
        this.onTokenRequest = onTokenRequest;
        this.disposed = false;
        this.inboxUrl = baseClientDefaults.baseUrl + notifyPrefix + '/inbox/api';
    }
    /**
     * GET /inbox/api/v1/notifications
     * */
    getList(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = Object.assign({}, request);
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications${toUrlQueryString(params)}`, {
                method: 'GET',
            });
            if (response.ok) {
                return yield response.json();
            }
            else {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * GET /inbox/api/v1/notifications/{notificationId}
     * */
    getNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/${notificationId}`, {
                method: 'GET',
            });
            if (response.ok) {
                return response.json();
            }
            else {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * PUT /inbox/api/v1/notifications/read
     * */
    markAsRead(notificationIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/read`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ids: notificationIds,
                }),
            });
            if (!response.ok) {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * PUT looped /inbox/api/v1/notifications/read
     * */
    markAllAsRead(archived) {
        return __awaiter(this, void 0, void 0, function* () {
            let areUnread = true;
            while (areUnread) {
                const getResponse = yield this.getList({
                    size: 500,
                    read: false,
                    archived: archived,
                });
                areUnread = getResponse.items.length > 0;
                if (areUnread) {
                    yield this.markAsRead(getResponse.items.map((i) => i.id));
                }
            }
        });
    }
    /**
     * PUT /inbox/api/v1/notifications/archive
     * */
    archive(notificationIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/archive`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ids: notificationIds,
                }),
            });
            if (!response.ok) {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * DELETE /inbox/api/v1/notifications/archive
     * */
    unArchive(notificationIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/archive`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ids: notificationIds,
                }),
            });
            if (!response.ok) {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * PUT /inbox/api/v1/notifications/{notificationId}/response
     * */
    saveResponse(notificationId, responseValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/${notificationId}/response`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ response: responseValue }),
            });
            if (!response.ok) {
                throw yield createClientError(response);
            }
        });
    }
    /**
     * POST /inbox/api/v1/notifications/{notificationId}/attachments/{attachmentKey}/signatures
     * */
    createAttachmentSignature(notificationId, attachmentKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchInternal(`${this.inboxUrl}/v1/notifications/${notificationId}/attachments/${attachmentKey}/signatures`, {
                method: 'POST',
            });
            if (response.ok) {
                return response.json();
            }
            else {
                throw yield createClientError(response);
            }
        });
    }
    getDownloadAttachmentUrl(notificationId, attachmentKey, signature) {
        return `${this.inboxUrl}/v1/notifications/${notificationId}/attachments/${attachmentKey}?signature=${encodeURIComponent(signature)}`;
    }
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.onTokenRequest();
            storeToken(this.email, token);
            return token;
        });
    }
    getToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = getToken(this.email);
            if (!token) {
                return this.refreshToken();
            }
            return token;
        });
    }
    withDefaultFetchOptions(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = new Headers(options.headers);
            headers.set('Authorization', 'Bearer ' + (yield this.getToken()));
            options.headers = headers;
            return options;
        });
    }
    fetchInternal(url, init) {
        return __awaiter(this, void 0, void 0, function* () {
            const fetchCall = () => __awaiter(this, void 0, void 0, function* () {
                const options = yield this.withDefaultFetchOptions(init !== null && init !== void 0 ? init : {});
                return fetch(url, options);
            });
            const response = yield fetchCall();
            if (response.status === 401) {
                yield this.refreshToken();
                return fetchCall();
            }
            return response;
        });
    }
}
